import React, {useState, useRef, useEffect, CSSProperties} from 'react';
import styles from './SelectFiltered.module.css';
import {SelectOption} from "./types/SelectOption.interface.ts";
import {arrowDownIcon, closeIcon} from "../../assets/icons/icons.ts";
import {cn} from "../../utils/cn.ts";
import {Icon} from "../General/Icon/Icon.tsx";
import { useScreenDetector } from '../../hooks/useScreenDetector.ts';

interface Props {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    shownArrow?: boolean;
    shownSearchIcon?: boolean;
    rootClassName?: string;
    selectClassName?: string;
    styles?: string|CSSProperties
}

export const SelectFiltered: React.FC<Props> = ({
                                            rootClassName,
                                            selectClassName,
                                            options,
                                            value,
                                            onChange,
                                            shownArrow = true,
                                            shownSearchIcon = false,
                                            placeholder = '---',
                                        }) => {

    const [isOpen, setIsOpen] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [searchValue, setSearchValue] = useState(value);
    const [selectedValue, setSelectedValue] = useState(value);
    const [limit, setLimit] = useState(8);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const {isMobile, isDesktop} = useScreenDetector();
    
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        if(options.length === 1) {
            setDisabled(true);
            setSelectedValue(options[0].value);
        } else {
            if(value) {
                let needUpdate= true;
                options.map(item => {
                    if(item.value === value) needUpdate = false;
                });
                if(needUpdate) {
                    setSelectedValue('');
                    setSearchValue('');
                } else {
                    setSelectedValue(value);
                    setSearchValue(value);
                }
            }
            setDisabled(false);
        }
    }, [options]);

    const handleOptionSelect = (selectedValue: string): void => {
        onChange(selectedValue);
        setIsOpen(false);
        setSelectedValue(selectedValue);
        setSearchValue('');
    }

    const handleClickOutside = (event: MouseEvent): void => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    }

    function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setSelectedValue(event.target.value);
        setSearchValue(event.target.value);
        setIsOpen(true);
    }

    const filteredOptions = options.filter(option =>
        option.value.toLowerCase().includes(searchValue.toLowerCase())
    );

    return (
        <div ref={containerRef} className={cn(styles.container, rootClassName)}>{}
            {
                options.length || searchValue
                ? <div className={cn(styles.select, selectClassName, isOpen && styles.isOpen)} onClick={() =>setIsOpen(!isOpen)}>
                    {shownSearchIcon && <Icon name='search' width={18} height={18} color='#fff' />}
                    <input
                        type='text'
                        placeholder={placeholder}
                        className={cn(styles.searchInput, isOpen && styles.isOpen)}
                        ref={inputRef}
                        value={selectedValue}
                        onChange={handleSearchChange}
                        disabled={disabled}
                    />
                    {shownArrow && (<div
                        className={cn(
                            styles.arrow,
                            isOpen && styles.arrowOpen
                        )}
                    >
                        <img width={14} height={16} src={arrowDownIcon} alt='Стрелка вниз'/>
                    </div>)}
                </div>
                : null
            }

            {isOpen && isDesktop && (
                <ul className={styles.dropdown}>
                    {filteredOptions.map((option, key) => {
                        if(limit && key >= limit) return null;
                        return (
                        <li
                            key={option.id}
                            onClick={() => handleOptionSelect(option.value)}
                            className={cn(
                                styles.option,
                                value === option.value && styles.optionSelected,
                            )}
                        >
                            {option.value}
                        </li>
                    )})}
                    {filteredOptions.length > limit && (
                        <li
                            className={styles.option}
                            onClick={() => setLimit(0)}
                        >
                            развернуть...
                        </li>
                    )}
                    {filteredOptions.length === 0 && (
                        <div className={styles.noOptions}>Ничего не найдено</div>
                    )}
                </ul>
            )}
            {isOpen && isMobile && (
                <div className={cn(styles.drawer, isOpen && styles.drawerOpen)}>
                    <div className={styles.header}>
                        <div className={styles.headerTitle}>
                            {placeholder}
                        </div>
                        <img className={styles.closeBtn} src={closeIcon} onClick={() => setIsOpen(false)}/>
                    </div>
                    <div className={styles.content}>
                        <div style={{padding: 16, marginBottom: 12,}}>
                            <div className={styles.select}>
                            {shownSearchIcon && <Icon name='search' width={18} height={18} color='#fff' />}
                                <input
                                    type='text'
                                    placeholder={placeholder}
                                    className={cn(styles.searchInput)}
                                    value={selectedValue}
                                    onChange={handleSearchChange}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                        {filteredOptions.map((option) => {
                            const value = searchValue && searchValue.toLowerCase() !== option.value.toLowerCase() ? option.value
                            .replace( new RegExp( searchValue + '(?!([^<]+)?<)', 'gi'),
                            "<span style=\"color: #F39200;font-weight: 700\">$&</span>"
                        ) : option.value
                        return (
                            <div className={cn(styles.drawerFilters, searchValue.toLowerCase() === option.value.toLowerCase() && styles.drawerFiltersSelected)}
                                key={option.id}
                                onClick={() => handleOptionSelect(option.value)}
                                dangerouslySetInnerHTML={{__html: value}}
                            />
                        )})}
                    </div>
                </div>
            )}
        </div>
    );
};
